// StorageAccessService.js

/**
 * StorageAccessService.js
 * 
 * Service to access storage URLs with signed URLs and caching.
 */

// Firebase
import { FIREBASE_PROJECT_ID, functions, httpsCallable } from 'firebaseConfig';

// Services
import { isHostedPageAudience, logPageVideoDebug } from 'page/pageVideoBlockUtils';

// Constants
const SIGNED_URL_FN = 'getSignedStorageUrl';
const HLS_MANIFEST_FN = 'getSecureHlsManifestUrl';

// Cache for signed URLs
const signedUrlCache = new Map();

// Tracks active signed URL fetches to dedupe concurrent requests.
const signedUrlInFlight = new Map();

// Cache for manifest URLs
const manifestUrlCache = new Map();

// Tracks active manifest URL fetches to dedupe concurrent requests.
const manifestUrlInFlight = new Map();

// Cache for public page video manifest URLs.
const publicPageManifestUrlCache = new Map();

// Tracks active public page manifest URL fetches.
const publicPageManifestUrlInFlight = new Map();

// Cache for recent failures to avoid spamming the network/console.
const signedUrlFailureCache = new Map();

/**
 * Gets the current time in milliseconds.
 * 
 * @returns {number} Current time in milliseconds.
 */
function nowMs() {
  return Date.now();
}

/**
 * Gets a cached value if it exists and is not expired.
 * 
 * @param {Map} map - The cache map.
 * @param {string} key - The cache key.
 * 
 * @returns {any|null} - The cached value or null if not found/expired.
 */
function getCached(map, key) {

  // Check for existing entry
  const hit = map.get(key);

  // Not found
  if (!hit) return null;

  // Expired
  if (hit.expiresAtMs && hit.expiresAtMs < nowMs()) {
    map.delete(key);
    return null;
  }

  // Valid cache hit
  return hit.value;
}

function getStorageBucketFromUrl(url) {
  if (!url || typeof url !== 'string') return null;

  const trimmed = String(url).trim();
  const firebaseMatch = trimmed.match(/firebasestorage\.googleapis\.com\/v0\/b\/([^/]+)\//i);
  if (firebaseMatch && firebaseMatch[1]) return firebaseMatch[1];

  const gcsMatch = trimmed.match(/storage\.googleapis\.com\/([^/]+)\//i);
  if (gcsMatch && gcsMatch[1]) return gcsMatch[1];

  return null;
}

function normalizeStorageProjectId(bucketOrProject) {
  return String(bucketOrProject || '')
    .trim()
    .toLowerCase()
    .replace(/\.firebasestorage\.app$/i, '')
    .replace(/\.appspot\.com$/i, '');
}

function isCurrentProjectStorageUrl(url) {
  const urlBucket = getStorageBucketFromUrl(url);
  if (!urlBucket) return true;

  return normalizeStorageProjectId(urlBucket) === normalizeStorageProjectId(FIREBASE_PROJECT_ID);
}

function isProbablySignedOrPublicUrl(url) {
  if (!url || typeof url !== 'string') return false;
  const u = String(url);

  // Local object URLs
  if (u.startsWith('blob:') || u.startsWith('data:')) return true;

  // Already a full URL (not a raw storagePath)
  if (!u.includes('://')) return false;

  // Firebase Storage download URLs usually include a token and are directly usable.
  // Example: .../o/<path>?alt=media&token=...
  if (/\btoken=/.test(u)) return isCurrentProjectStorageUrl(u);

  // GCS V4 signed URL params
  if (/\bX-Goog-(Algorithm|Credential|Date|Expires|Signature)=/i.test(u)) {
    return isCurrentProjectStorageUrl(u);
  }

  // Older GCS signed URL params
  if (/\b(GoogleAccessId|Signature|Expires)=/i.test(u)) return isCurrentProjectStorageUrl(u);

  return false;
}

// Sets a value in the cache with optional TTL.
function setCached(map, key, value, ttlMs) {
  map.set(key, {
    value,
    expiresAtMs: ttlMs ? (nowMs() + ttlMs) : null,
  });
}

/**
 * Infers the storage object path from a given URL.
 * 
 * Supports raw storage paths, GCS URLs, and Firebase Storage URLs.
 * 
 * @param {string} url - The input URL.
 * 
 * @returns {string|null} - The inferred storage object path or null if not found.
 */
export function inferStoragePathFromUrl(url) {

  // Basic validation
  if (!url || typeof url !== 'string') return null;

  // Trim whitespace
  const trimmed = String(url).trim();

  // Strip query and fragment for raw paths
  const withoutQuery = trimmed.split('?')[0].split('#')[0];

  // Raw storage object paths (preferred now that Storage reads are locked down)
  // e.g. media/<secureFolder>/<nodeKey>/original.jpg or uploads/<...>
  if (
    withoutQuery &&
    !withoutQuery.includes('://') &&
    !withoutQuery.startsWith('gs://') &&
    !withoutQuery.startsWith('blob:') &&
    !withoutQuery.startsWith('data:')
  ) {
    const normalized = withoutQuery.replace(/^\/+/, '');
    if (
      normalized.startsWith('media/') ||
      normalized.startsWith('uploads/') ||
      normalized.startsWith('products/') ||
      normalized.startsWith('nodes/') ||
      normalized.startsWith('bases/') ||
      normalized.startsWith('nodetypes/') ||
      normalized.startsWith('help/')
    ) {
      return normalized;
    }
  }

  // https://storage.googleapis.com/<bucket>/<path>
  const gcs = url.match(/^https:\/\/storage\.googleapis\.com\/([^/]+)\/(.+)$/i);
  if (gcs && gcs[2]) {
    // Signed GCS URLs include query params; they are not part of the object name.
    const objectPath = String(gcs[2]).split('?')[0].split('#')[0];
    return decodeURIComponent(objectPath);
  }

  // https://firebasestorage.googleapis.com/v0/b/<bucket>/o/<encodedPath>?...
  const fb = url.match(/^https:\/\/firebasestorage\.googleapis\.com\/v0\/b\/[^/]+\/o\/([^?]+)(\?.*)?$/i);
  if (fb && fb[1]) {
    return decodeURIComponent(fb[1]);
  }

  return null;
}

/**
 * Gets a signed URL for accessing a storage object.
 * 
 * @param {Object} params - Parameters object.
 * @param {string} [params.nodeKey] - The node key (optional).
 * @param {string} params.storagePath - The storage object path.
 * @param {number} [params.ttlSeconds] - Time-to-live in seconds for the signed URL (optional).
 * 
 * @returns {Promise<string|null>} - The signed URL or null if not available.
 */
export async function getSignedUrlForStoragePath({ nodeKey, baseKey, storagePath, ttlSeconds } = {}) {

  // Validate input
  if (!storagePath) return null;

  // Cache key
  const ttlKey = typeof ttlSeconds === 'number' ? ttlSeconds : 'default';

  // Check cache
  const cacheKey = `${nodeKey || 'none'}::${baseKey || 'none'}::${storagePath}::${ttlKey}`;

  // Check cache
  const cached = getCached(signedUrlCache, cacheKey);

  // Return cached if available
  if (cached) return cached;

  // If we recently failed to sign this exact thing, don't hammer the endpoint.
  const recentFailure = getCached(signedUrlFailureCache, cacheKey);
  if (recentFailure) return null;

  // If another caller is already resolving this key, reuse that promise.
  const inFlight = signedUrlInFlight.get(cacheKey);
  if (inFlight) return inFlight;

  const task = (async () => {
    // Call the Cloud Function to get a signed URL
    const call = httpsCallable(functions, SIGNED_URL_FN);

    try {
      // Make the call
      const result = await call({
        nodeKey: nodeKey || null,
        baseKey: baseKey || null,
        storagePath,
        ttlSeconds: ttlSeconds || null});

      // Extract URL
      const url = result?.data?.url || null;

      // Default signed URLs are 4 hours; cache slightly less to avoid edge expiry.
      setCached(signedUrlCache, cacheKey, url, 3.5 * 60 * 60 * 1000);

      return url;
    } catch (e) {
      // Avoid repeated spam for the same key; try again later.
      setCached(signedUrlFailureCache, cacheKey, true, 30 * 1000);
      return null;
    } finally {
      signedUrlInFlight.delete(cacheKey);
    }
  })();

  signedUrlInFlight.set(cacheKey, task);
  return task;
}

/**
 * Resolves an image URL, returning a signed URL if the image is stored in Storage.
 * 
 * @param {Object} params - Parameters object.
 * @param {string} [params.nodeKey] - The node key (optional).
 * @param {string} [params.url] - The original image URL (optional).
 * @param {string} [params.storagePath] - The storage object path (optional).
 * @param {number} [params.ttlSeconds] - Time-to-live in seconds for the signed URL (optional).
 * 
 * @returns {Promise<string|null>} - The resolved image URL.
 */
export async function resolveImageUrl({ nodeKey, baseKey, url, storagePath, ttlSeconds } = {}) {

  // Respect an explicit storagePath so callers can force a fresh signed URL
  // even if url is currently a tokenized/signed URL that may be stale.
  const explicitPath = typeof storagePath === 'string' ? String(storagePath).trim() : '';

  // If it already looks like a directly usable signed/public URL, don't re-sign it.
  // This prevents unnecessary callable requests (and noise) for Firebase download-token URLs.
  if (!explicitPath && isProbablySignedOrPublicUrl(url)) return url;

  // Infer storage path if not provided
  const inferred = inferStoragePathFromUrl(url);

  // Determine effective storage path
  const effectivePath = explicitPath || inferred;

  // For node-scoped paths (e.g. nodes/<nodeKey>/cover/...), infer nodeKey from the path
  // if not explicitly provided, so the media gate can authorize access.
  let effectiveNodeKey = nodeKey || null;
  let effectiveBaseKey = baseKey || null;
  if (!effectiveNodeKey && typeof effectivePath === 'string') {
    const m = effectivePath.match(/^nodes\/([^/]+)\//);
    if (m && m[1]) effectiveNodeKey = m[1];

    // Media objects are generally stored under media/<secureFolder>/<nodeKey>/...
    // Infer nodeKey when possible to reduce failed sign attempts.
    const mediaMatch = effectivePath.match(/^media\/[^/]+\/([^/]+)\//);
    if (!effectiveNodeKey && mediaMatch && mediaMatch[1]) {
      effectiveNodeKey = mediaMatch[1];
    }
  }

  if (typeof effectivePath === 'string') {
    const baseMatch = effectivePath.match(/^(?:bases|bases)\/([^/]+)\//);
    if (baseMatch && baseMatch[1]) {
      effectiveBaseKey = baseMatch[1];
    }
  }

  // If we have a storage path, get a signed URL
  if (effectivePath) {
    return getSignedUrlForStoragePath({
      nodeKey: effectiveNodeKey,
      baseKey: effectiveBaseKey,
      storagePath: effectivePath,
      ttlSeconds});
  }

  // Otherwise, return the original URL
  return url || null;
}

/**
 * Resolves a file URL, returning a signed URL if the file is stored in Storage.
 * 
 * @param {Object} params - Parameters object.
 * @param {string} [params.nodeKey] - The node key (optional).
 * @param {string} [params.url] - The original file URL (optional).
 * @param {string} [params.storagePath] - The storage object path (optional).
 * @param {number} [params.ttlSeconds] - Time-to-live in seconds for the signed URL (optional).
 * 
 * @returns {Promise<string|null>} - The resolved file URL.
 */
export async function resolveFileUrl({ nodeKey, url, storagePath, ttlSeconds } = {}) {
  return resolveImageUrl({ nodeKey, url, storagePath, ttlSeconds });
}

/**
 * Gets a secure HLS manifest URL for streaming.
 * 
 * @param {Object} params - Parameters object.
 * @param {string} params.nodeKey - The node key.
 * @param {string} params.manifestPath - The storage path to the HLS manifest.
 * 
 * @returns {Promise<string|null>} - The secure HLS manifest URL.
 */
export async function getSecureHlsManifestUrl({ nodeKey, manifestPath } = {}) {

  // Validate input
  if (!nodeKey || !manifestPath) return null;

  // Cache key
  const cacheKey = `${nodeKey}::${manifestPath}`;

  // Check cache
  const cached = getCached(manifestUrlCache, cacheKey);

  // Return cached if available
  if (cached) return cached;

  // Reuse active request for the same manifest.
  const inFlight = manifestUrlInFlight.get(cacheKey);
  if (inFlight) return inFlight;

  const task = (async () => {
    // Call the Cloud Function to get the secure HLS manifest URL
    const call = httpsCallable(functions, HLS_MANIFEST_FN);

    // Make the call
    const result = await call({ nodeKey, manifestPath });

    // Extract URL
    const url = result?.data?.url || null;
    const normalizedUrl = normalizeSecureHlsManifestUrlForWindowOrigin(url);

    // Ticket TTL is 10 minutes; cache slightly less.
    setCached(manifestUrlCache, cacheKey, normalizedUrl, 9 * 60 * 1000);

    return normalizedUrl;
  })();

  manifestUrlInFlight.set(cacheKey, task);

  try {
    // Return URL
    return await task;
  } finally {
    manifestUrlInFlight.delete(cacheKey);
  }
}

function isHlsManifestReference(value) {
  const normalized = String(value || '').trim();
  return normalized.includes('.m3u8') || normalized.includes('/secureHlsManifest?ticket=');
}

function isLocalDevHostname(hostname) {
  const host = String(hostname || '').trim().toLowerCase();
  return host === 'localhost' || host === '127.0.0.1';
}

function normalizeSecureHlsManifestUrlForWindowOrigin(url) {
  if (typeof window === 'undefined' || typeof url !== 'string' || !url.includes('/secureHlsManifest')) {
    return url;
  }

  try {
    const resolved = new URL(url);
    const current = new URL(window.location.href);

    if (isLocalDevHostname(current.hostname)) {
      const params = new URLSearchParams(resolved.search);
      const query = params.toString();
      return `${current.origin}/secureHlsManifest${query ? `?${query}` : ''}`;
    }

    if (resolved.origin === current.origin) {
      return url;
    }

    resolved.protocol = current.protocol;
    resolved.host = current.host;
    return resolved.toString();
  } catch (_error) {
    return url;
  }
}

function normalizePagePlaybackContext(pagePlaybackContext) {
  if (!isHostedPageAudience(pagePlaybackContext)) {
    return null;
  }

  const basePath = String(pagePlaybackContext.basePath || '').trim();
  const nodePath = String(pagePlaybackContext.nodePath || '').trim();
  const pagePath = String(pagePlaybackContext.pagePath || '').trim();

  if (!basePath || !nodePath || !pagePath) {
    return null;
  }

  return {
    nodePath,
    pin: String(pagePlaybackContext.pin || '').trim(),
    pagePath,
    basePath,
  };
}

async function resolvePublicPageVideoManifestUrl({ nodeKey, pagePlaybackContext } = {}) {
  const context = normalizePagePlaybackContext(pagePlaybackContext);
  if (!context || !nodeKey) return null;

  const cacheKey = [
    context.basePath,
    context.nodePath,
    context.pagePath,
    nodeKey,
    context.pin,
  ].join('::');

  const cached = getCached(publicPageManifestUrlCache, cacheKey);
  if (cached) return normalizeSecureHlsManifestUrlForWindowOrigin(cached);

  const inFlight = publicPageManifestUrlInFlight.get(cacheKey);
  if (inFlight) return inFlight;

  const task = (async () => {
    try {
      // Public Pages product removed — hosted page video manifests are unavailable.
      const url = null;

      if (url) {
        const normalizedUrl = normalizeSecureHlsManifestUrlForWindowOrigin(url);
        setCached(publicPageManifestUrlCache, cacheKey, normalizedUrl, 9 * 60 * 1000);
        logPageVideoDebug('manifest-url-ready', {
          nodeKey,
          normalizedUrl,
          pinLength: context.pin.length,
          pagePath: context.pagePath,
          sourceUrl: url,
        });
        return normalizedUrl;
      } else {
        logPageVideoDebug('manifest-url-empty', {
          nodeKey,
          pinLength: context.pin.length,
          pagePath: context.pagePath,
        });
      }

      return url;
    } catch (error) {
      logPageVideoDebug('manifest-fetch-failed', {
        code: error?.code || '',
        message: error?.message || 'Unknown error',
        nodeKey,
        pinLength: context.pin.length,
        pagePath: context.pagePath,
        status: error?.status || null,
      });
      throw error;
    }
  })();

  publicPageManifestUrlInFlight.set(cacheKey, task);

  try {
    return await task;
  } finally {
    publicPageManifestUrlInFlight.delete(cacheKey);
  }
}

/**
 * Resolves a playable video URL, returning a secure HLS manifest URL or signed URL as needed.
 * 
 * @param {Object} node - The video node object.
 * @param {Object|null} [pagePlaybackContext] - Public page playback context when viewing anonymously.
 * 
 * @returns {Promise<string|null>} - The playable video URL.
 */
export async function resolvePlayableVideoUrl(node, pagePlaybackContext = null) {

  // Validate input
  if (!node) return null;

  // Extract relevant fields
  const nodeKey = node.key || null;

  // Determine raw URL and storage path
  const rawUrl = node.url || node.data_video?.url || null;

  // Determine storage path
  const storagePath = node.storagePath || node.data_video?.storagePath || null;

  // If already a secure manifest URL, return as is
  if (typeof rawUrl === 'string' && rawUrl.includes('/secureHlsManifest?ticket=')) {
    return rawUrl;
  }

  // Infer storage path if not provided
  const inferred = inferStoragePathFromUrl(rawUrl);

  // Determine effective storage path
  const effectivePath = storagePath || inferred;

  // Check if HLS
  const isHls = (typeof rawUrl === 'string' && rawUrl.includes('.m3u8'))
    || (typeof effectivePath === 'string' && effectivePath.includes('.m3u8'));

  // Return appropriate URL
  if (isHls && nodeKey && effectivePath) {
    const publicContext = normalizePagePlaybackContext(pagePlaybackContext);
    if (publicContext) {
      logPageVideoDebug('resolve-via-public-manifest', {
        manifestPath: effectivePath,
        nodeKey,
        pinLength: publicContext.pin.length,
      });
      const manifestUrl = await resolvePublicPageVideoManifestUrl({ nodeKey, pagePlaybackContext });
      logPageVideoDebug('playback-url-resolved', {
        manifestUrl,
        nodeKey,
        via: 'public-manifest',
      });
      return manifestUrl;
    }

    if (isHostedPageAudience(pagePlaybackContext)) {
      logPageVideoDebug('resolve-public-context-missing', {
        manifestPath: effectivePath,
        nodeKey,
        nodePath: pagePlaybackContext?.nodePath || '',
        pagePath: pagePlaybackContext?.pagePath || '',
        basePath: pagePlaybackContext?.basePath || '',
      });
    }

    const manifestUrl = await getSecureHlsManifestUrl({ nodeKey, manifestPath: effectivePath });
    logPageVideoDebug('playback-url-resolved', {
      manifestUrl,
      nodeKey,
      via: 'secure-hls-callable',
    });
    return manifestUrl;
  }

  // Never return a direct Storage manifest URL for HLS; segment requests would 403.
  if (isHls) {
    logPageVideoDebug('playback-url-unresolved-hls', {
      effectivePath,
      nodeKey,
    });
    return null;
  }

  // Otherwise, return signed URL if storage path exists
  if (effectivePath) {
    return getSignedUrlForStoragePath({ nodeKey, storagePath: effectivePath });
  }

  // Otherwise, return raw URL when it is not an HLS manifest reference
  if (isHlsManifestReference(rawUrl)) {
    return null;
  }

  return rawUrl;
}
